import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertTriangle, RefreshCw, Send } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function AdminAppHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  const load = () => axios.get(`${BACKEND}/api/admin/monitor/status`, auth()).then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);

  const act = async (kind) => {
    setBusy(kind); setNote('');
    try {
      if (kind === 'run') { await axios.post(`${BACKEND}/api/admin/monitor/run-now`, {}, auth()); await load(); setNote('Checks complete.'); }
      else { const r = await axios.post(`${BACKEND}/api/admin/monitor/test-alert`, {}, auth()); setNote(`Test alert sent via: ${(r.data.sent || []).join(' + ') || 'nothing (check ALERT_PHONE / SMS setup)'}`); }
    } catch (e) { setNote(e.response?.data?.detail || 'Request failed'); }
    setBusy('');
  };

  const results = data?.lastRun?.results || {};
  const open = (data?.incidents || []).filter((i) => i.status === 'open');

  return (
    <div className="space-y-6" data-testid="admin-app-health">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Activity className="h-6 w-6 text-emerald-600" /> App Health</h2>
        <span className="text-sm text-slate-500">Checked automatically every 15 minutes · last run {fmt(data?.lastRun?.at)}</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => act('test')} disabled={!!busy} data-testid="monitor-test-alert-btn"><Send className="h-4 w-4 mr-2" />Send test alert</Button>
          <Button onClick={() => act('run')} disabled={!!busy} data-testid="monitor-run-now-btn"><RefreshCw className={`h-4 w-4 mr-2 ${busy === 'run' ? 'animate-spin' : ''}`} />Run checks now</Button>
        </div>
      </div>
      {note && <p className="text-sm text-slate-700" data-testid="monitor-note">{note}</p>}

      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(results).map(([key, r]) => (
          <div key={key} className={`rounded-xl border p-4 bg-white ${r.ok ? 'border-emerald-200' : 'border-rose-300'}`} data-testid={`monitor-check-${key}`}>
            <div className="flex items-center gap-2 font-semibold">
              {r.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-rose-600" />}
              {r.label}
            </div>
            <p className="text-sm text-slate-600 mt-1">{r.detail}</p>
          </div>
        ))}
        {!Object.keys(results).length && <p className="text-sm text-slate-500">No checks have run yet. Click “Run checks now”.</p>}
      </div>

      <div className="rounded-xl border bg-white p-4">
        <h3 className="font-semibold mb-2">Incidents ({open.length} open)</h3>
        <div className="divide-y text-sm">
          {(data?.incidents || []).map((i, idx) => (
            <div key={idx} className="py-2 flex flex-wrap gap-x-4" data-testid={`monitor-incident-${idx}`}>
              <span className={`font-semibold ${i.status === 'open' ? 'text-rose-600' : 'text-emerald-600'}`}>{i.status === 'open' ? 'OPEN' : 'RESOLVED'}</span>
              <span className="font-medium">{i.label}</span>
              <span className="text-slate-500">since {fmt(i.firstSeenAt)}{i.resolvedAt ? ` · fixed ${fmt(i.resolvedAt)}` : ''}</span>
              <span className="w-full text-slate-600">{i.detail}</span>
            </div>
          ))}
          {!(data?.incidents || []).length && <p className="text-slate-500">No incidents recorded.</p>}
        </div>
      </div>
    </div>
  );
}
